<template>
    <div class="p-5 bg-echoes-background-black">
        <UsersHeader>
            <template #title>Sales Credentials</template>
            <template #description>
                Manage and organize sales credentials
            </template>
        </UsersHeader>
        <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light">
            <!-- Search and Filters -->
            <div class="p-4">
                <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div class="w-full sm:w-96">
                        <TextInput 
                            v-model="searchQuery" 
                            placeholder="Search by listing name" 
                            class="w-full"
                        />
                    </div>
                    <div class="w-full sm:w-24">
                        <SelectInput
                            v-model="itemsPerPage"
                            :options="itemsPerPageOptions"
                            :hide-search="true"
                            @update:modelValue="currentPage = 1"
                        />
                    </div>
                </div>
            </div>
            <!-- Table -->
            <div>
                <table class="min-w-full divide-y divide-echoes-border-light">
                    <thead class="bg-echoes-border-light">
                        <tr>
                            <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                Listing
                            </th>
                            <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                Seller
                            </th>
                            <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                Buyer
                            </th>
                            <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                        <template v-if="loading">
                            <!-- Skeleton loading rows -->
                            <tr v-for="i in itemsPerPage" :key="`skeleton-${i}`" class="bg-echoes-background-black/80">
                                <td class="px-6 py-4">
                                    <div class="animate-pulse">
                                        <div class="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-48"></div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="flex items-center animate-pulse">
                                        <div class="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full dark:bg-gray-700"></div>
                                        <div class="ml-4">
                                            <div class="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-32 mb-2"></div>
                                            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="flex items-center animate-pulse">
                                        <div class="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full dark:bg-gray-700"></div>
                                        <div class="ml-4">
                                            <div class="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-32 mb-2"></div>
                                            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="flex items-center justify-center animate-pulse">
                                        <div class="h-8 bg-gray-200 rounded-md dark:bg-gray-700 w-16"></div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template v-else v-for="credential in paginatedCredentials" :key="credential.id">
                            <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-300">
                                <td class="px-6 py-4">
                                    <div class="text-sm font-medium text-white">
                                        {{ credential.sale.listing.name }}
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-10 w-10">
                                            <img v-if="credential.sale.listing.datasets[0].seller.user.profile_pic" :src="credential.sale.listing.datasets[0].seller.user.profile_pic" alt="User Profile" class="h-10 w-10 rounded-full">
                                            <User v-else class="h-10 w-10 rounded-full bg-echoes-primary" />
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-medium text-white">
                                                {{ credential.sale.listing.datasets[0].seller.user.name }}
                                            </div>
                                            <div class="text-sm text-white/80">
                                                {{ credential.sale.listing.datasets[0].seller.user.email }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-10 w-10">
                                            <img v-if="credential.sale.buyer.profile_pic" :src="credential.sale.buyer.profile_pic" alt="User Profile" class="h-10 w-10 rounded-full">
                                            <User v-else class="h-10 w-10 rounded-full bg-echoes-primary" />
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-medium text-white">
                                                {{ credential.sale.buyer.name }}
                                            </div>
                                            <div class="text-sm text-white/80">
                                                {{ credential.sale.buyer.email }}
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                <td class="px-6 py-4">
                                <div class="flex items-center justify-center">
                                    <button @click="openCredentialsModal(credential)" 
                                    class="flex items-center gap-2 px-2.5 py-1 text-gray-100 rounded-md bg-echoes-primary hover:bg-echoes-primary/80 text-xs"
                                    :class="isDisabledButton(credential.sale.history) ? 'bg-gray-500 opacity-40 cursor-not-allowed' : 'bg-echoes-primary hover:bg-echoes-primary/8'" :disabled="isDisabledButton(credential.sale.history)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
                                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>Review
                                    </button>
                                </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
                
                <!-- Pagination Footer -->
                <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center text-sm text-white/80">
                            <span>Showing</span>
                            <span class="font-medium mx-1">
                                {{ Math.min(currentPage * itemsPerPage, paginatedCredentials.length) }}
                            </span>
                            <span>of</span>
                            <span class="font-medium ml-1">{{ totalItems }}</span>
                        </div>
                        <div class="flex items-center space-x-2">
                            <button
                                @click="currentPage = Math.max(1, currentPage - 1)"
                                :disabled="currentPage === 1"
                                class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <div class="flex items-center space-x-1">
                                <template v-for="page in totalPages" :key="page">
                                    <button
                                        @click="currentPage = page"
                                        :class="[
                                            'px-3 py-1 rounded-lg text-sm font-medium',
                                            currentPage === page
                                                ? 'bg-echoes-primary text-white'
                                                : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                        ]"
                                    >
                                        {{ page }}
                                    </button>
                                </template>
                            </div>
                            <button
                                @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                :disabled="currentPage === totalPages"
                                class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-if="credentialsModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] w-full">
        <div class="bg-white dark:bg-echoes-background-gray rounded-lg p-6 max-w-7xl w-full mx-4 " >
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">External Dataset Credentials</h3>
                <button @click="closeCredentialsModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>
            <div class="flex flex-col gap-2">
                <div class="field-input-group-item-dataset-name w-full">
                    <div class="field-input-group-item-label-container">
                        <InputError :message="errors.newCredentails" class="label-error" />
                    </div>
                    <div class="grid grid-cols-4 gap-2">
                        <div class="col-span-1">
                            <SaleHistoryCard :sale="credentialSelected.sale" />
                        </div>
                        <div class="col-span-3 bg-echoes-background-black rounded-md">
                            <MarkdownRenderer v-model="credentialSelected.credentials" :is-editing="false" :content-only="true" class="max-h-[350px] p-4" />
                        </div>
                    </div>
                </div>
            </div>
            <div>
            <div class="w-full mt-4">
                <TextInput 
                    v-model="responseMessage" 
                    placeholder="Enter your response message" 
                    class="w-full"
                />
            </div>
            </div>
            <div class="flex justify-between mt-4 gap-2 text-xs tracking-normal">
                <button @click="updateCredentials('reject')" class="p-2 pb-1 bg-echoes-black text-white border-b-2 hover:text-echoes-primary hover:border-echoes-primary">
                    Reject
                </button>
                <div class="flex justify-end gap-2">
                    <button @click="updateCredentials('needs_context')" class="px-2 bg-echoes-primary text-white rounded-md hover:bg-echoes-primary/80">
                        Needs Context
                    </button>
                    <button @click="updateCredentials('approve')" class="px-4 py-2 bg-echoes-primary text-white rounded-md hover:bg-echoes-primary/80">
                        Approve
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div v-if="confirmRejectModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] w-full">
        <div class="flex flex-col bg-white dark:bg-echoes-background-gray rounded-lg p-6">
            <div class=" max-w-7xl w-full mx-4 " >
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Are you sure you want to reject this credential?</h3>
            </div>
            <div class="flex justify-between mt-4 gap-2 text-sm tracking-normal font-bold">
                <button @click="confirmRejectModal = false" class="text-echoes-primary hover:text-white">
                    No
                </button>
                <button @click="handleConfirmReject" class="px-2 py-1 bg-echoes-primary text-white rounded-md hover:bg-echoes-primary/80">
                    Yes
                </button>
            </div>
        </div>
    </div>

</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from '@/plugins/axios'
import MarkdownRenderer from '../../components/MarkdownRenderer.vue';
import { useLayoutComponents } from '@/composables/layout.js';
import SaleHistoryCard from '@/components/cards/SaleHistoryCard.vue';
import { useToast } from '@/composables/use-toast';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
const { TextInput, SelectInput, InputError } = useLayoutComponents();
const { addToast } = useToast();

const paginatedCredentials = ref([]);
const credentialsModalOpen = ref(false);
const errors = ref({});
const credentialSelected = ref(null);
const searchQuery = ref('');
const itemsPerPage = ref(10);

const itemsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 }
];
const currentPage = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);
const loading = ref(false);
const responseMessage = ref('');

const getCredentials = async () => {
    loading.value = true;
    const query = `skip=${(currentPage.value - 1) * itemsPerPage.value}&take=${itemsPerPage.value}&search=${searchQuery.value}`;
    const response = await axios.get(`/api/sale-credentials/to-review?${query}`);
    paginatedCredentials.value = response.data.saleCredentials;
    totalItems.value = response.data.total;
    totalPages.value = Math.ceil(response.data.total / itemsPerPage.value);
    loading.value = false;
}

const openCredentialsModal = (credential) => {
    credentialSelected.value = credential;
    credentialsModalOpen.value = true;
}

const closeCredentialsModal = () => {
    credentialsModalOpen.value = false;
    credentialSelected.value = null;
}

const confirmReject = ref(false);
const confirmRejectModal = ref(false);
const handleConfirmReject = () => {
    confirmRejectModal.value = false;
    confirmReject.value = true;
    updateCredentials('reject');
}

const updateCredentials = async (action) => {
    if(action === 'needs_context') {
        if(responseMessage.value.length === 0) {
            addToast('Please enter a response message', 'error');
            return;
        }
    }
    if(action === 'reject' && !confirmReject.value) {
        confirmRejectModal.value = true;
        return;
    }
    const response = await axios.post(`/api/sale-credentials/review`, {
        id: credentialSelected.value.id,
        action: action,
        message: responseMessage.value !== '' ? responseMessage.value : action.charAt(0).toUpperCase() + action.slice(1)
    });
    if(response.status === 200 || response.status === 201) {
        getCredentials();
        closeCredentialsModal();
    }
}


const isDisabledButton = (history) => {
    const lastHistory = history[history.length - 1];
    return lastHistory.status.name !== 'reviewing' && lastHistory.step.name !== 'seller_review'

}

watch(currentPage, () => {
    getCredentials();
});

watch(itemsPerPage, () => {
    getCredentials();
});

watch(searchQuery, (newValue) => {
    if(newValue.length > 3 || newValue.length === 0) {
        currentPage.value = 1;
        getCredentials();
    }
});

onMounted(() => {
    getCredentials();
})
</script>